import { createMessages, createMessage, SB } from '@play-co/replicant';
import { Duo } from '.';
import { breakJapaneseText, isJapanese } from '../utils/string';
import { petSchema } from './duo.schema';

export default (Duo: Duo) =>
  createMessages(SB.object({ ...Duo.schema }))({
    sendDuoInvitation: createMessage(
      SB.object({
        name: SB.string(),
        timestamp: SB.number(),
        item: SB.string(),
        senderPet: petSchema,
      }),
      (state, { name, timestamp, item, senderPet }, info) => {
        const received = state.duo.invitations.received;

        // Do not add duplicate invitations.
        if (
          received.some(
            ({ timestamp: receivedTimestamp, playerId: receivedId }) =>
              receivedTimestamp === timestamp && receivedId === info.senderId,
          )
        ) {
          return;
        }

        received.push({
          timestamp,
          playerId: info.senderId,
          careItemId: item,
          status: 'pending',
          pet: senderPet,
          lastReminder: 0,
        });

        state.duo.lastReceivedInvitation = {
          timestamp,
          playerId: info.senderId,
          name,
        };
      },
    ),

    sendDuoResponse: createMessage(
      SB.object({
        name: SB.string(),
        status: SB.tuple(['cancelled', 'declined', 'accepted'] as const),
        message: SB.string().optional(),
        newSpecies: SB.string().optional(),
        timestamp: SB.number(),
      }),
      (state, { name, status, message, newSpecies, timestamp }, info) => {
        const isTargetInvitation = ({
          playerId,
          timestamp: invitationTimestamp,
        }: {
          playerId: string;
          timestamp: number;
        }) => playerId === info.senderId && timestamp === invitationTimestamp;
        let invitation = state.duo.invitations.sent.find(isTargetInvitation);

        let cancelledInvitation = status === 'cancelled';
        // Also search for received invitations. Since it can be cancelled by the inviter.
        if (!invitation) {
          invitation = state.duo.invitations.received.find(isTargetInvitation);
          // Declined sent invitations are processed as cancelled.
          cancelledInvitation = true;
        }

        // We don't expect this to happen. But do not throw. Since this would prevent the game from starting.
        if (!invitation) {
          return;
        }

        invitation.status = cancelledInvitation ? 'cancelled' : status;
        const accepted = status === 'accepted';

        // If message is in japanese, apply custom line break logic.
        // If applied to english text, it would break words. Which is not desired, but acceptable in case of a false positive.
        invitation.message = isJapanese(message)
          ? breakJapaneseText(message)
          : message;

        // Cancelled invitations are not handled by the invitee.
        if (cancelledInvitation) return;

        // Set last response.
        if (!state.duo.lastResponse?.accepted) {
          state.duo.lastResponse = {
            name,
            timestamp: invitation.timestamp,
            playerId: info.senderId,
            accepted,
          };
        }

        if (!accepted) return;

        // Set debut data.
        state.duo.debut = {
          species: newSpecies,
          rewardClaimed: false,
          careItemId: invitation.careItemId,
          inviterId: state.id,
          inviteeId: invitation.playerId,
        };

        // Clear other invitations if accepted.
        state.duo.invitations.sent = [invitation];
        state.duo.invitations.received = [];
      },
    ),
  });
